import { useCallback, useEffect, useMemo, useState } from 'react';
import type { Navegacion, Operaciones, Yo } from '../api/operaciones';
import type { CajaSinConexion, TaquillaSinConexion, VentaGuardada } from '../pantallas/taquilla/venta';
import { articulosDeTaquilla, cupoSinConexion, elementosDeVenta, ventasPorEnviar, type Copia, type Pendiente } from './copiaPuerta';
import { useCopia } from './useCopia';
import { paisDelNegocio } from '../pantallas/taquilla/pais';

/**
 * La Taquilla de la tablet **sin conexión** (10.7; G2, G4, G509), con la misma copia de la puerta que la Puerta (6.2):
 *
 * - `cajaDeLaCopia`: el evento de la copia, la parte del cupo de esta tablet (`box_office.quota − used`) menos lo vendido
 *   aún sin enviar, y los tipos y precios que se venden sin conexión (`box_office.items`).
 * - `useTaquillaSinConexion`: guarda la venta en la tablet (con «Entra ya», sus llegadas) y, al volver la conexión, la
 *   envía sola en el lote de la puerta (`syncDoorPackage`); Pases la reenvía a Eventos, que crea el pedido.
 * - `datosDeLaSesion`: la cuenta y el menú de la persona (`getMe`, `getMyNavigation`); los últimos leídos se recuerdan en
 *   memoria por sesión para abrir la Taquilla también sin conexión (la Puerta los lee al abrirse), y con ellos el país
 *   del negocio para el prefijo del móvil (G975, `paisDelNegocio`).
 */
export function cajaDeLaCopia(copia: Copia | null, pendientes: readonly Pendiente[]): CajaSinConexion | null {
    const ref = copia?.access_group.ref;
    if (copia == null || ref?.type !== 'evento') {
        return null;
    }
    return {
        evento: ref.id,
        nombre: copia.access_group.label,
        cupo: copia.box_office.quota,
        quedan: cupoSinConexion(copia.box_office, pendientes),
        sinEnviar: ventasPorEnviar(pendientes).ventas,
        articulos: articulosDeTaquilla(copia),
    };
}

const enLinea = () => (typeof navigator === 'undefined' ? true : navigator.onLine);

export function useTaquillaSinConexion({ ops, opsTablet, negocio, miembro, conexion, almacen }: {
    ops: Operaciones;
    opsTablet: Operaciones;
    negocio: string;
    miembro: string;
    conexion?: boolean;
    almacen?: Pick<Storage, 'getItem' | 'setItem' | 'removeItem'> | null;
}): TaquillaSinConexion {
    const [delNavegador, setDelNavegador] = useState(enLinea);
    useEffect(() => {
        const si = () => { setDelNavegador(true); };
        const no = () => { setDelNavegador(false); };
        window.addEventListener('online', si);
        window.addEventListener('offline', no);
        return () => {
            window.removeEventListener('online', si);
            window.removeEventListener('offline', no);
        };
    }, []);
    const conConexion = conexion ?? delNavegador;
    const copia = useCopia({ ops, opsTablet, negocio, conConexion, ...(almacen === undefined ? {} : { almacen }) });
    const { anotar } = copia;
    const vender = useCallback((v: VentaGuardada) => { anotar(elementosDeVenta(v, miembro, new Date())); }, [anotar, miembro]);
    const caja = useMemo(() => cajaDeLaCopia(copia.estado.copia, copia.estado.pendientes), [copia.estado.copia, copia.estado.pendientes]);
    // G974: las ventas que el servidor aceptó sin su contacto, para que la taquilla avise de que no se enviaron sus entradas.
    const sinContacto = copia.estado.resultado?.sinContacto ?? 0;
    // G986: el país del negocio viaja con la copia, para el prefijo del móvil aunque la tablet nunca haya leído `getBusiness`.
    return { conConexion, caja, vender, sinContacto, olvidarSinContacto: copia.olvidarResultado, pais: copia.estado.pais };
}

export interface DatosDeLaSesion {
    yo: Yo;
    navegacion: Navegacion;
}

const recordados = new Map<string, DatosDeLaSesion>();

/**
 * La cuenta y el menú de la persona, pedidos cada vez; si no se pueden pedir (sin conexión), los últimos leídos en esta
 * sesión y negocio.
 */
export async function datosDeLaSesion(ops: Operaciones, negocio: string, token: string): Promise<DatosDeLaSesion> {
    const clave = `${token}|${negocio}`;
    try {
        // El país del negocio queda leído para el prefijo del móvil en la Taquilla sin conexión (G975).
        void paisDelNegocio(ops, negocio);
        const [yo, navegacion] = await Promise.all([ops.getMe(), ops.getMyNavigation(negocio)]);
        recordados.set(clave, { yo, navegacion });
        return { yo, navegacion };
    } catch (e) {
        const ya = recordados.get(clave);
        if (ya === undefined) {
            throw e;
        }
        return ya;
    }
}
